import {
    DailyScrumCommentWithReplies,
    DailyScrumItem,
    DailyScrumItemWithInfo,
    DailyScrumWithItems,
} from '../../../common/types/databaseTypes';
import { Database, supabase } from '@tickitacka/shared';

export type DailyScrumInsert =
    Database['public']['Tables']['daily_scrum']['Insert'];
export type DailyScrumItemInsert =
    Database['public']['Tables']['daily_scrum_item']['Insert'];
export type DailyScrumItemUpdate =
    Database['public']['Tables']['daily_scrum_item']['Update'];

export type CreateDailyScrumParams = Omit<
    DailyScrumInsert,
    'id' | 'register_date' | 'update_date' | 'enable'
>;

export type CreateDailyScrumItemParams = Omit<
    DailyScrumItemInsert,
    'id' | 'register_date' | 'update_date' | 'enable'
> & {
    team_id?: string;
    date?: string;
};

export const dailyScrumService = {
    /**
     * 특정 날짜의 데일리스크럼 정보를 불러옵니다
     * @param date 조회할 날짜 (YYYY-MM-DD 형식)
     * @param teamId 팀 ID
     * @returns 데일리스크럼 정보와 해당 항목들
     */
    async getDailyScrumByDate(
        date: string,
        teamId: string
    ): Promise<DailyScrumWithItems | null> {
        try {
            // 해당 날짜와 팀의 데일리스크럼 조회
            const { data, error } = await supabase
                .from('daily_scrum')
                .select(
                    `
                *,
                team:team!team_id (
                    id,
                    name,
                    profile_image_url
                ),
                daily_scrum_items:daily_scrum_item!daily_scrum_id (
                    *,
                    user:user!user_id (
                        id,
                        name,
                        profile_image_url
                    ),
                    comments:daily_scrum_comment!daily_scrum_item_id (
                        *,
                        user:user!register_id (
                            id,
                            name,
                            profile_image_url
                        ),
                        parent_comment:daily_scrum_comment!parent_comment_id (
                            id
                        )
                    ),
                    emoji_reactions:daily_scrum_item_emoji_reaction!daily_scrum_item_id (
                        id,
                        emoji_code,
                        register_id,
                        register_date,
                        user:user!register_id(
                            id,
                            name,
                            profile_image_url
                        )
                    )
                )
                `
                )
                .eq('team_id', teamId)
                .eq('date', date)
                .eq('daily_scrum_items.enable', true)
                .eq('daily_scrum_items.comments.enable', true)
                .maybeSingle();

            if (error) throw error;
            if (!data) return null;

            // 댓글을 계층 구조로 처리
            const processedData = {
                ...data,
                daily_scrum_items: (data.daily_scrum_items || [])
                    .sort((a, b) => {
                        return (
                            new Date(b.register_date).getTime() -
                            new Date(a.register_date).getTime()
                        );
                    })
                    .map(item => {
                        // 모든 댓글을 가져와서 필터링
                        const allComments = (item.comments || []).filter(
                            comment => comment.enable !== false
                        );

                        // 루트 댓글만 필터링 (parent_comment_id가 null인 댓글)
                        const rootComments = allComments
                            .filter(
                                comment => comment.parent_comment_id === null
                            )
                            .map(comment => ({
                                ...comment,
                                user: comment.user,
                                replies: [] as DailyScrumCommentWithReplies[], // 빈 배열로 초기화
                            }));

                        // 답글을 찾아서 부모 댓글의 replies에 추가
                        const commentsWithReplies = rootComments.map(
                            rootComment => {
                                const replies = allComments
                                    .filter(
                                        reply =>
                                            reply.parent_comment_id ===
                                            rootComment.id
                                    )
                                    .map(reply => ({
                                        ...reply,
                                        user: reply.user,
                                    }));

                                return {
                                    ...rootComment,
                                    replies,
                                };
                            }
                        );

                        // 이모지 리액션에 필요한 필드 추가
                        const processedEmojis = (
                            item.emoji_reactions || []
                        ).map(emoji => ({
                            emoji_code: emoji.emoji_code,
                            user: emoji.user,
                            register_date: emoji.register_date,
                        }));

                        return {
                            ...item,
                            comments: commentsWithReplies,
                            emoji_reactions: processedEmojis,
                        };
                    }),
            };

            return processedData;
        } catch (error) {
            console.error(
                `Failed to fetch daily scrum for date ${date} and team ${teamId}:`,
                error
            );
            throw error;
        }
    },

    /**
     * 새 데일리 스크럼 아이템을 생성합니다 (필요한 경우 데일리 스크럼을 자동 생성)
     */
    async createDailyScrumItem(
        scrumItemData: CreateDailyScrumItemParams
    ): Promise<DailyScrumItem> {
        const { team_id, date, ...dailyScrumItem } = scrumItemData;

        const scrumData: CreateDailyScrumParams = {
            team_id: team_id,
            date: date,
            title: `Daily Scrum - ${date}`,
            is_completed: false,
            register_id: dailyScrumItem.user_id,
            update_id: dailyScrumItem.user_id,
        };

        const scrumId =
            dailyScrumItem.daily_scrum_id ||
            (await (async () => {
                if (!team_id || !date) {
                    throw new Error(
                        'team_id and date are required when daily_scrum_id is not provided'
                    );
                }

                const { data: dailyScrum, error } = await supabase
                    .from('daily_scrum')
                    .upsert(scrumData, {
                        onConflict: 'team_id,date',
                    })
                    .select()
                    .single();

                if (error) {
                    throw new Error(
                        `Failed to find or create daily scrum: ${error.message}`
                    );
                }

                return dailyScrum.id;
            })());

        const { data: result, error } = await supabase
            .from('daily_scrum_item')
            .insert({
                ...dailyScrumItem,
                daily_scrum_id: scrumId,
                register_date: new Date().toISOString(),
                update_date: new Date().toISOString(),
            })
            .select()
            .single();

        if (error) {
            throw new Error(
                `Failed to create daily scrum item: ${error.message}`
            );
        }

        return result;
    },

    /**
     * 데일리 스크럼 아이템을 업데이트합니다
     */
    async updateDailyScrumItem(
        id: string,
        data: DailyScrumItemUpdate
    ): Promise<DailyScrumItem> {
        const { data: result, error } = await supabase
            .from('daily_scrum_item')
            .update({
                ...data,
                update_date: new Date().toISOString(),
            })
            .eq('id', id)
            .select()
            .single();

        if (error) {
            throw new Error(
                `Failed to update daily scrum item: ${error.message}`
            );
        }

        return result;
    },

    /**
     * 데일리 스크럼 아이템을 삭제합니다 (완전 삭제)
     */
    async deleteDailyScrumItem(id: string): Promise<void> {
        const { error } = await supabase
            .from('daily_scrum_item')
            .delete()
            .eq('id', id);

        if (error) {
            throw new Error(
                `Failed to delete daily scrum item: ${error.message}`
            );
        }
    },

    /**
     * 데일리스크럼 아이템 댓글 알림 발송을 위한 데이터 조회
     */
    async getDailyScrumItemById(id: string): Promise<DailyScrumItemWithInfo> {
        const { data, error } = await supabase
            .from('daily_scrum_item')
            .select(
                `
                *,
                daily_scrum:daily_scrum_id(
                    id,
                    team_id,
                    date
                )
            `
            )
            .eq('id', id)
            .single();

        if (error) {
            throw new Error(
                `Failed to fetch daily scrum by item id: ${error.message}`
            );
        }

        return data;
    },
};
